import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import {
  clearCrmLeadOfferHandoff,
  resolveCrmLeadOfferHandoff,
} from "../../../utils/crmLeadHandoff";
import "./CrmLeadOfferSearchBanner.css";

/**
 * Top strip on agent search/offer flows: clearly shows search is for a CRM lead.
 */
export default function CrmLeadOfferSearchBanner({ sideBarMargin = "0px" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const handoff = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return resolveCrmLeadOfferHandoff(params);
  }, [location.search]);

  const path = location.pathname || "";
  const onRelevantPath =
    path.includes("searchoffer") ||
    path.includes("/hotel/offer/") ||
    path.includes("/dynamic/offer/") ||
    path.includes("/flights/offer/");

  const onClear = useCallback(() => {
    clearCrmLeadOfferHandoff();
    setDismissed(true);
    const params = new URLSearchParams(location.search || "");
    if (params.has("crmLeadId")) {
      params.delete("crmLeadId");
      params.delete("crmLeadName");
      params.delete("crmLeadPhone");
      params.delete("crmLeadEmail");
      params.delete("crmLeadDest");
      const q = params.toString();
      navigate(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const onBackToLead = useCallback(() => {
    const id = handoff?.crmLeadId;
    if (!id) return;
    const tab =
      handoff?.returnTab === "dockets" ? "dockets" : "leads";
    navigate(
      `/p/dashboard/drafts?tab=${tab}&leadId=${encodeURIComponent(id)}`
    );
  }, [handoff?.crmLeadId, handoff?.returnTab, navigate]);

  if (dismissed || !onRelevantPath || !handoff?.crmLeadId) return null;

  const name = String(handoff.full_name || "").trim() || "ליד CRM";
  const bits = [];
  if (handoff.phone) bits.push(handoff.phone);
  if (handoff.email) bits.push(handoff.email);
  if (handoff.interest_destination) bits.push(handoff.interest_destination);

  return (
    <div
      className="crm-lead-offer-search-banner"
      style={{
        marginRight: sideBarMargin,
        width: `calc(100% - ${sideBarMargin})`,
      }}
      role="status"
    >
      <div className="crm-lead-offer-search-banner__main">
        <span className="crm-lead-offer-search-banner__tag">חיפוש עבור ליד</span>
        <strong className="crm-lead-offer-search-banner__name">{name}</strong>
        {bits.length ? (
          <span className="crm-lead-offer-search-banner__meta">
            {bits.join(" · ")}
          </span>
        ) : null}
        <span className="crm-lead-offer-search-banner__hint">
          כל הצעה (PDF / WhatsApp / קישור) תירשם ביומן פעולות בדוקט — כולל דמי טיפול
        </span>
      </div>
      <div className="crm-lead-offer-search-banner__actions">
        <Button size="small" type="default" onClick={onBackToLead}>
          חזרה לתיק
        </Button>
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined />}
          aria-label="סיום חיפוש לליד"
          onClick={onClear}
        />
      </div>
    </div>
  );
}
